/**
 * CallAudioService
 * Generates telephone ring tones using the Web Audio API.
 * No audio files required — tones are synthesised in-browser.
 *
 * Patterns (UK-style):
 *   ring     — 400 ms ON · 200 ms OFF · 400 ms ON · 2 000 ms OFF  (recipient)
 *   ringback — same cadence, slightly lower pitch                   (caller)
 */

type ToneType = 'ring' | 'ringback';

interface Burst {
  onMs: number;
  offMs: number;
}

interface ToneConfig {
  /** Primary frequency in Hz */
  freqA: number;
  /** Optional secondary frequency mixed in (gives a richer telephone sound) */
  freqB?: number;
  /** Gain 0–1 */
  gain: number;
  /** Burst pattern — repeated in order, then the whole cycle loops */
  bursts: Burst[];
}

const CONFIGS: Record<ToneType, ToneConfig> = {
  // Recipient ringtone — bright, attention-grabbing
  ring: {
    freqA: 480,
    freqB: 620,
    gain: 0.22,
    bursts: [
      { onMs: 400, offMs: 200 },
      { onMs: 400, offMs: 2000 },
    ],
  },
  // Caller ringback — softer, indicating remote phone is ringing
  ringback: {
    freqA: 425,
    freqB: 450,
    gain: 0.12,
    bursts: [
      { onMs: 400, offMs: 200 },
      { onMs: 400, offMs: 2000 },
    ],
  },
};

class CallAudioServiceClass {
  private ctx: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private oscA: OscillatorNode | null = null;
  private oscB: OscillatorNode | null = null;
  private loopTimer: ReturnType<typeof setTimeout> | null = null;
  private active = false;
  private currentType: ToneType | null = null;

  // ── Public API ───────────────────────────────────────────────────────────

  play(type: ToneType) {
    if (this.active && this.currentType === type) return;
    this.stop();
    this.active = true;
    this.currentType = type;
    this._startLoop(CONFIGS[type]);
  }

  stop() {
    this.active = false;
    this.currentType = null;
    if (this.loopTimer) { clearTimeout(this.loopTimer); this.loopTimer = null; }
    this._teardownNodes();
  }

  // ── Internals ────────────────────────────────────────────────────────────

  private _getContext(): AudioContext {
    if (!this.ctx || this.ctx.state === 'closed') {
      this.ctx = new AudioContext();
    }
    // Resume if suspended (browser autoplay policy)
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  private _teardownNodes() {
    try { this.gainNode?.disconnect(); } catch { /* noop */ }
    try { this.oscA?.stop(); this.oscA?.disconnect(); } catch { /* noop */ }
    try { this.oscB?.stop(); this.oscB?.disconnect(); } catch { /* noop */ }
    this.gainNode = null;
    this.oscA = null;
    this.oscB = null;
  }

  private _startLoop(config: ToneConfig) {
    this._teardownNodes();

    const ctx = this._getContext();

    // Build graph: oscA (+ optional oscB) → gain → destination
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.connect(ctx.destination);
    this.gainNode = gain;

    const oscA = ctx.createOscillator();
    oscA.type = 'sine';
    oscA.frequency.value = config.freqA;
    oscA.connect(gain);
    oscA.start();
    this.oscA = oscA;

    if (config.freqB) {
      const oscB = ctx.createOscillator();
      oscB.type = 'sine';
      oscB.frequency.value = config.freqB;
      // Mix second oscillator at slightly lower gain for blend
      const blendGain = ctx.createGain();
      blendGain.gain.value = 0.7;
      oscB.connect(blendGain);
      blendGain.connect(gain);
      oscB.start();
      this.oscB = oscB;
    }

    this._scheduleCycle(config, ctx.currentTime, gain, config.gain);
  }

  private _scheduleCycle(
    config: ToneConfig,
    startAt: number,
    gain: GainNode,
    peakGain: number,
  ) {
    if (!this.active) return;

    const ctx = this._getContext();
    let t = startAt;

    for (const burst of config.bursts) {
      // Fade in quickly (5 ms) to avoid click
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(peakGain, t + 0.005);
      t += burst.onMs / 1000;

      // Fade out quickly (5 ms)
      gain.gain.linearRampToValueAtTime(0, t + 0.005);
      t += burst.offMs / 1000;
    }

    // Schedule next cycle ~50 ms before it's due (gives time to set up)
    const msUntilNextCycle = Math.max(0, (t - ctx.currentTime) * 1000 - 50);

    this.loopTimer = setTimeout(() => {
      if (this.active && this.gainNode === gain) {
        this._scheduleCycle(config, t, gain, peakGain);
      }
    }, msUntilNextCycle);
  }

  // ── Call-ended voice ────────────────────────────────────────────────────────

  playCallEnded() {
    if (!('speechSynthesis' in window)) return;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance('Call ended');
    utterance.volume = 0.9;
    utterance.rate = 0.95;
    utterance.pitch = 1.0;

    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(
      (v) =>
        v.lang.startsWith('en') &&
        (v.name.includes('Samantha') ||
          v.name.includes('Karen') ||
          v.name.includes('Daniel') ||
          v.name.includes('Google UK') ||
          v.name.includes('Google US')),
    );
    if (preferred) utterance.voice = preferred;

    window.speechSynthesis.speak(utterance);
  }
}

export const CallAudioService = new CallAudioServiceClass();
